import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AUDIT_LOGS_LOAD, AUDIT_LOGS_UNLOAD } from "../../constants/actionTypes";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import agent from "../../agent";
import {Dropdown} from "primereact/dropdown";

function safeParseJson(value) {
    if (value == null) return null;
    if (typeof value === "object") return value;
    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
}

function formatDate(value) {
    if (!value) return "";
    try {
        const d = new Date(value);
        return d.toLocaleString("ru-RU");
    } catch (e) {
        return String(value);
    }
}

function toApiDateString(date) {
    if (!(date instanceof Date) || isNaN(date)) return undefined;
    const pad = (n) => String(n).padStart(2, '0');
    const dd = pad(date.getDate());
    const MM = pad(date.getMonth() + 1);
    const yyyy = date.getFullYear();
    const HH = pad(date.getHours());
    const mm = pad(date.getMinutes());
    return `${dd}.${MM}.${yyyy} ${HH}:${mm}`;
}

function AuditLogs() {
    const dispatch = useDispatch();
    const logsPage = useSelector((state) => state?.auditLogs?.logs || { content: [], number: 0, size: 20, totalElements: 0, totalPages: 0 });
    const items = Array.isArray(logsPage?.content) ? logsPage.content : [];
    const page = Number.isFinite(logsPage?.number) ? logsPage.number : 0;
    const size = Number.isFinite(logsPage?.size) ? logsPage.size : 20;
    const totalElements = Number.isFinite(logsPage?.totalElements) ? logsPage.totalElements : items.length;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Filters
    const [userEmail, setUserEmail] = useState("");
    const [from, setFrom] = useState(null); // Date | null
    const [to, setTo] = useState(null); // Date | null
    const [server, setServer] = useState("SLQ"); // 'SLQ' | 'PTC'

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalJson, setModalJson] = useState(null);

    const filters = useMemo(() => {
        const f = {};
        if (userEmail && userEmail.trim()) f.email = userEmail.trim();
        const fromStr = toApiDateString(from);
        const toStr = toApiDateString(to);
        if (fromStr) f.from = fromStr;
        if (toStr) f.to = toStr;
        if (server) {
            f.server = server;
        }
        return f;
    }, [userEmail, from, to, server]);

    const loadAuditLogs = async (pageArg = page, sizeArg = size) => {
        setLoading(true);
        setError("");
        try {
            const data = await agent.AuditLogs.getAuditLogs(pageArg, sizeArg, filters);
            dispatch({ type: AUDIT_LOGS_LOAD, payload: data });
        } catch (e) {
            const msg = agent.handleError ? agent.handleError(e) : "Произошла ошибка. Попробуйте позже.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // initial load
        loadAuditLogs(0, size);
        return () => dispatch({ type: AUDIT_LOGS_UNLOAD });
    }, []);

    const applyFilters = () => {
        loadAuditLogs(0, size);
    };

    const resetFilters = () => {
        setUserEmail("");
        setFrom(null);
        setTo(null);
        setServer("SLQ");
        loadAuditLogs(0, size);
    };

    const openMessage = (item) => {
        const parsed = safeParseJson(item?.message);
        setModalJson(parsed);
        setModalTitle(`Сообщение для заказа ${item?.message?.orderNum || item?.orderNum || ""}`);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setModalJson(null);
        setModalTitle("");
    };

    const onPage = (e) => {
        const pageIndex = Math.floor(e.first / e.rows);
        const rows = e.rows || size;
        loadAuditLogs(pageIndex, rows);
    };

    return (
        <div style={{ padding: 16 }}>
            <h2>Журнал аудита</h2>

            {/* Фильтры */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <label>Сервер</label>
                    <Dropdown
                        value={server}
                        onChange={(e) => setServer(e.value)}
                        options={[{ label: 'SLQ', value: 'SLQ' }, { label: 'PTC', value: 'PTC' }]}
                        placeholder="Выберите сервер"
                        style={{ minWidth: 180 }}
                    />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <label>Электронная почта пользователя</label>
                    <InputText
                        placeholder="user@example.com"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                    />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <label>Дата и время от</label>
                    <Calendar
                        value={from}
                        onChange={(e) => setFrom(e.value)}
                        showTime
                        hourFormat="24"
                        showIcon
                        locale='ru'
                        placeholder="dd.MM.yyyy HH:mm"
                        dateFormat="dd.mm.yy"
                        mask="99.99.9999 99:99"
                    />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <label>Дата и время до</label>
                    <Calendar
                        value={to}
                        onChange={(e) => setTo(e.value)}
                        showTime
                        hourFormat="24"
                        showIcon
                        locale='ru'
                        placeholder="dd.MM.yyyy HH:mm"
                        dateFormat="dd.mm.yy"
                        mask="99.99.9999 99:99"
                    />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <Button label="Применить" icon="pi pi-search" onClick={applyFilters} />
                    <Button label="Сбросить" icon="pi pi-filter-slash" severity="secondary" onClick={resetFilters} />
                </div>
            </div>


            {/* Состояние загрузки / ошибки */}
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="6" />
                </div>
            )}
            {error && !loading && (
                <Message severity="error" text={error} style={{ marginBottom: 12 }} />
            )}

            {/* Таблица */}
            {!loading && items && items.length > 0 && (
                <DataTable
                    value={items}
                    lazy
                    totalRecords={totalElements}
                    paginator
                    rows={size}
                    first={page * size}
                    onPage={onPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    loading={loading}
                    dataKey="id"
                    rowHover
                    emptyMessage="Данные не найдены"
                    currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                >
                    <Column header="Дата и время" body={(row) => formatDate(row?.createdAt)} />
                    <Column field="userEmail" header="Email" />
                    <Column field="ip" header="IP" />
                    <Column header="Тип задачи" body={(row) => {
                        const msg = safeParseJson(row?.message);
                        return msg?.taskType || row?.taskType || (msg?.message?.taskType) || "";
                    }} />
                    <Column header="Номер заказа" body={(row) => {
                        const msg = safeParseJson(row?.message);
                        return msg?.orderNum || row?.orderNum || "";
                    }} />
                    <Column header="Действия" body={(row) => (
                        <Button label="Показать сообщение" icon="pi pi-eye" onClick={() => openMessage(row)} text />
                    )} />
                </DataTable>
            )}

            {!loading && (!items || items.length === 0) && (
                <div>Нет данных</div>
            )}

            {/* Модальное окно */}
            <Dialog header={modalTitle || "Сообщение"} visible={showModal} onHide={closeModal} style={{ width: '60vw' }} modal>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {typeof modalJson === 'string' ? modalJson : JSON.stringify(modalJson, null, 2)}
        </pre>
            </Dialog>
        </div>
    );
}

export default AuditLogs;